import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Star, MapPin } from 'lucide-react';
import type { Venue } from '../types';
import { VenueImageGallery } from '../components/VenueImageGallery';
import { cn } from '../lib/utils';
import { buildOutboundVenuePath } from '../lib/routes';
import { getPublicVenueTherapySlug } from '../lib/publicTherapies';
import {
  getVenueAmenitySignals,
  getVenueLocationDisplay,
  getVenueOfferHighlights,
  getVenuePriceSignal,
  getVenueReviewHighlight,
} from '../lib/venuePresentation';

interface VenueCardProps {
  venue: Venue;
  index?: number;
  badgeLabel?: string;
  featured?: boolean;
}

export function VenueCard({ venue, index = 0, badgeLabel, featured = false }: VenueCardProps) {
  const location = useLocation();
  const detailHref = `/${venue.city}/${getPublicVenueTherapySlug(venue.therapy, venue.primaryTherapy)}/${venue.slug}`;
  const primaryDestinationType = venue.website ? 'website' : venue.bookingUrl ? 'booking' : null;
  const primaryExternalHref = primaryDestinationType
    ? buildOutboundVenuePath(venue.slug, primaryDestinationType, `${location.pathname}${location.search}`)
    : null;
  const primaryActionLabel = primaryDestinationType === 'booking' ? 'Book now' : 'Website';
  const locationLabel = getVenueLocationDisplay(venue);
  const offerHighlights = getVenueOfferHighlights(venue, 2);
  const amenitySignals = getVenueAmenitySignals(venue, 1);
  const reviewHighlight = venue.verified ? getVenueReviewHighlight(venue) : null;
  const secondarySignals = [
    venue.facilityType?.toLowerCase().includes('outdoor') ? 'Outdoor' : null,
    ...amenitySignals,
  ].filter((value, index, array): value is string => Boolean(value) && array.indexOf(value) === index);
  const featurePills = [...offerHighlights, ...secondarySignals].slice(0, 3);
  const priceSignal = getVenuePriceSignal(venue.priceRange);
  const reviewCountLabel = venue.reviewCount > 0
    ? `${venue.reviewCount} ${venue.reviewCount === 1 ? 'review' : 'reviews'}`
    : null;
  const primaryCtaLabel = 'Website';
  const descriptionText = reviewHighlight || venue.longDescription?.trim() || venue.description?.trim() || null;

  return (
    <article
      className={cn(
        'group flex h-full flex-col rounded-[1.25rem] border border-[rgba(196,105,79,0.12)] bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(255,255,255,0.72))] p-3.5 shadow-[0_12px_32px_rgba(26,26,26,0.04)] transition-all duration-300 hover:-translate-y-[2px] hover:border-[rgba(196,105,79,0.22)] hover:shadow-[0_18px_38px_rgba(26,26,26,0.06)] sm:rounded-[1.5rem] sm:p-4',
        featured && 'relative border-[rgba(196,105,79,0.18)] shadow-[0_16px_36px_rgba(196,105,79,0.06)]',
      )}
    >
      <VenueImageGallery
        venueName={venue.name}
        href={detailHref}
        images={[venue.image, ...(venue.images ?? [])]}
        aspectClassName="aspect-[5/4]"
        className={cn(
          'mb-3.5 rounded-[1.1rem] sm:mb-4 sm:rounded-[1.25rem]',
          featured && 'ring-1 ring-[rgba(196,105,79,0.22)]',
        )}
        showPhotoCount={false}
        topLeft={
          <div className="flex flex-col items-start gap-1.5">
            {badgeLabel ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/18 bg-[#C4694F]/88 px-2.5 py-1 backdrop-blur-md shadow-[0_8px_20px_rgba(0,0,0,0.18)]">
                <span className="text-[8px] font-bold uppercase tracking-[0.14em] leading-none text-white">
                  {badgeLabel}
                </span>
              </div>
            ) : null}
          </div>
        }
        topRight={
          <div className="inline-flex items-center gap-1.5 rounded-full border border-white/18 bg-black/36 px-3 py-1.5 backdrop-blur-md">
            <Star className="h-3 w-3 fill-amber-300 text-amber-300" />
            <span className="text-[12px] font-bold leading-none text-white">{venue.rating}</span>
          </div>
        }
      />

      <div className="flex flex-1 flex-col">
        <div className="mb-1.5 flex items-start justify-between gap-3">
          <h3 className="font-serif text-[1.12rem] leading-[1.12] tracking-[-0.01em] text-brand-charcoal transition-colors duration-200 group-hover:text-brand-sage sm:text-[1.22rem]">
            <Link to={detailHref}>{venue.name}</Link>
          </h3>
          {venue.logoUrl ? (
            <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/80">
              <img
                src={venue.logoUrl}
                alt={`${venue.name} logo`}
                className="h-full w-full object-contain p-0.5"
                onError={(e) => {
                  e.currentTarget.parentElement!.style.display = 'none';
                }}
              />
            </div>
          ) : null}
        </div>

        <div className="mb-3 flex items-center gap-1.5 text-[11px] font-medium text-brand-charcoal/52">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{locationLabel}</span>
        </div>

        {typeof venue.distanceMiles === 'number' ? (
          <div className="mb-3 inline-flex w-fit items-center rounded-full border border-brand-charcoal/10 bg-brand-charcoal/[0.03] px-3 py-1.5 text-[11px] font-semibold text-brand-charcoal/66">
            {venue.distanceMiles.toFixed(1)} miles away
          </div>
        ) : null}

        <div className="mb-3 flex flex-wrap items-center gap-2.5 border-t border-brand-charcoal/8 pt-3">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-charcoal/10 bg-brand-charcoal/[0.03] px-3 py-1.5">
            <Star className="h-3.5 w-3.5 fill-amber-300 text-amber-300" />
            <span className="text-[12px] font-semibold leading-none text-brand-charcoal">{venue.rating}</span>
            {reviewCountLabel ? (
              <span className="text-[11px] font-medium text-brand-charcoal/48">{reviewCountLabel}</span>
            ) : null}
          </div>
          <div className="inline-flex items-center rounded-full border border-brand-sage-green/30 bg-brand-sage-green/14 px-3 py-1.5">
            <span className="text-[11px] font-semibold leading-none text-brand-sage-green">{priceSignal}</span>
          </div>
        </div>

        {featurePills.length > 0 ? (
          <div className="mb-3 flex flex-wrap gap-2 border-t border-brand-charcoal/8 pt-3">
            {featurePills.map((item) => (
              <span
                key={item}
                className="inline-flex items-center rounded-full border border-[#C4694F]/18 bg-gradient-to-r from-[#C4694F]/10 via-[#D98A62]/8 to-[#F2C19A]/18 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-charcoal/78"
              >
                {item}
              </span>
            ))}
          </div>
        ) : null}

        {descriptionText ? (
          <div className="border-t border-brand-charcoal/8 pt-3">
            <p className="mb-3.5 line-clamp-4 text-[12.5px] leading-[1.68] text-brand-charcoal/66 sm:mb-4 sm:line-clamp-5 sm:text-[13px] sm:leading-[1.72]">
              {descriptionText}
            </p>
          </div>
        ) : null}

        <div className="flex-1" />

        <div className="flex items-center gap-2 border-t border-brand-charcoal/8 pt-3 sm:pt-3.5">
          <Link
            to={detailHref}
            className="inline-flex min-h-[38px] flex-1 items-center justify-center rounded-[0.85rem] border border-brand-charcoal/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(249,248,246,0.9)),url('/images/RD%20Section.png')] bg-cover bg-center px-3 py-2 text-[9px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/66 shadow-[0_8px_18px_rgba(26,26,26,0.04)] transition-all hover:-translate-y-[1px] hover:border-brand-charcoal/18 hover:text-brand-charcoal hover:shadow-[0_12px_22px_rgba(26,26,26,0.06)] sm:min-h-[40px] sm:rounded-[0.9rem] sm:px-3.5 sm:text-[10px]"
          >
            View details
          </Link>
          {primaryExternalHref ? (
            <a
              href={primaryExternalHref}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="rd-button-brand inline-flex min-h-[38px] flex-1 items-center justify-center gap-1 rounded-[0.85rem] px-3 py-2 text-[9px] font-bold uppercase tracking-[0.12em] hover:-translate-y-[1px] sm:min-h-[40px] sm:gap-1.5 sm:rounded-[0.9rem] sm:px-3.5 sm:text-[10px]"
            >
              {primaryActionLabel}
              <ArrowRight className="h-3 w-3" />
            </a>
          ) : (
            <span className="inline-flex min-h-[38px] flex-1 items-center justify-center gap-1 rounded-[0.85rem] border border-brand-charcoal/8 bg-brand-charcoal/[0.03] px-3 py-2 text-[9px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/40 sm:min-h-[40px] sm:gap-1.5 sm:rounded-[0.9rem] sm:px-3.5 sm:text-[10px]">
              {primaryCtaLabel}
              <ArrowRight className="h-3 w-3" />
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
